// ─────────────────────────────────────────
// InvitePage.js — invite landing page
// ─────────────────────────────────────────
// Visited via breakie.golf/invite/[username]
// Stores the inviter's username in localStorage so Signup.js
// can auto-follow them after account creation.

import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from '../components/AvatarImg'

export default function InvitePage() {
  const { username } = useParams()
  const [inviter, setInviter] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Remember who sent the invite — Signup.js will read this
    if (username) localStorage.setItem('looper_inviter', username.toLowerCase())

    // Fetch the inviter's public profile to personalise the page
    async function fetchInviter() {
      const { data } = await supabase
        .from('profiles')
        .select('username, full_name, avatar_emoji, avatar_url, handicap, location')
        .ilike('username', username)
        .single()
      setInviter(data || null)
      setLoading(false)
    }
    fetchInviter()
  }, [username])

  const displayName = inviter?.full_name || `@${inviter?.username || username}`

  return (
    <div style={st.page}>
      <div style={st.card}>

        {/* Logo */}
        <div style={st.logo}>BREAKIE</div>
        <div style={st.logoSub}>The golf social app</div>

        {/* Divider */}
        <div style={st.divider} />

        {/* Inviter info */}
        {!loading && inviter && (
          <div style={st.inviterBlock}>
            <div style={st.inviterAvatar}>
              <AvatarImg profile={inviter} />
            </div>
            <div style={st.inviterText}>
              <div style={st.inviterName}>{displayName}</div>
              {inviter.location && (
                <div style={st.inviterMeta}>📍 {inviter.location}</div>
              )}
              {inviter.handicap != null && (
                <div style={st.inviterMeta}>HCP {inviter.handicap}</div>
              )}
            </div>
          </div>
        )}

        {!loading && !inviter && (
          <div style={st.inviterBlock}>
            <div style={st.inviterAvatarFallback}>⛳</div>
            <div style={st.inviterText}>
              <div style={st.inviterName}>@{username}</div>
            </div>
          </div>
        )}

        {loading && <div style={st.loadingText}>Loading...</div>}

        {/* CTA */}
        <div style={st.headline}>
          {inviter
            ? `${displayName} invited you to join Breakie`
            : "You've been invited to join Breakie"}
        </div>
        <div style={st.sub}>
          Log rounds, track scores, compete with friends, and build your golf passport.
        </div>

        <Link to="/signup" style={st.signupBtn}>
          Create Free Account →
        </Link>

        <div style={st.loginRow}>
          Already have an account?{' '}
          <Link to="/login" style={st.loginLink}>Log in</Link>
        </div>

        {/* Feature pills */}
        <div style={st.features}>
          {['⛳ Log Rounds', '🏆 Leaderboards', '👥 Groups', '🗺️ Passport'].map(f => (
            <div key={f} style={st.featurePill}>{f}</div>
          ))}
        </div>

      </div>
    </div>
  )
}

const st = {
  page: {
    minHeight: '100vh', background: '#0e1f13',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, fontFamily: "'Barlow', sans-serif"
  },
  card: {
    background: '#1a3020', borderRadius: 28,
    padding: '44px 32px', width: '100%', maxWidth: 400,
    textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 48, letterSpacing: 4, color: '#c8f135',
    lineHeight: 1, marginBottom: 4
  },
  logoSub: { fontSize: 12, color: 'rgba(255,255,255,0.35)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 24 },
  divider: { height: 1, background: 'rgba(255,255,255,0.08)', marginBottom: 24 },

  inviterBlock: {
    display: 'flex', alignItems: 'center', gap: 14,
    background: 'rgba(255,255,255,0.05)', borderRadius: 16,
    padding: '14px 16px', marginBottom: 20, textAlign: 'left'
  },
  inviterAvatar: {
    width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#0e1f13)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', fontSize: 26
  },
  inviterAvatarFallback: {
    width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#0e1f13)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26
  },
  inviterText: { flex: 1 },
  inviterName: { fontWeight: 800, fontSize: 16, color: 'white', marginBottom: 2 },
  inviterMeta: { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 },

  loadingText: { color: 'rgba(255,255,255,0.3)', fontSize: 13, marginBottom: 20 },

  headline: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: 'white',
    lineHeight: 1.2, marginBottom: 10
  },
  sub: { fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, marginBottom: 28 },

  signupBtn: {
    display: 'block', background: '#c8f135', color: '#0e1f13',
    borderRadius: 14, padding: '16px', fontSize: 17,
    fontWeight: 900, cursor: 'pointer', textDecoration: 'none',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    marginBottom: 14
  },
  loginRow: { fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 28 },
  loginLink: { color: '#c8f135', fontWeight: 700, textDecoration: 'none' },

  features: { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  featurePill: {
    background: 'rgba(200,241,53,0.08)', borderRadius: 20,
    padding: '6px 14px', fontSize: 12, color: 'rgba(255,255,255,0.6)',
    fontWeight: 600
  }
}
